import { useMemo, useState } from 'preact/hooks';
import { useQuery } from '@tanstack/react-query';
import { route } from 'preact-router';

import { T, btnIcon, card } from '@/ui/tokens';
import { Icons, sized } from '@/ui/icons';
import { TopBar } from '@/ui/primitives/TopBar';
import { Chip } from '@/ui/primitives/Chip';
import { CoinGlyph } from '@/ui/primitives/CoinGlyph';
import { Spinner } from '@/ui/primitives/Spinner';
import { ErrorBox } from '@/ui/primitives/ErrorBox';
import { AnimatedList } from '@/ui/primitives/AnimatedList';
import { catalogRepo } from '@/data/repositories/catalog-repo';
import { fmt, dayBucket } from '@/ui/utils/format';
import { config, buildRoute } from '@/config';
import {
  classifyService, normalizeOrderState, orderState, orderTotalCost,
  type Order, type ServiceKind,
} from '@/domain/types';
import type { JSX } from 'preact';

type FiltKey = 'all' | 'active' | 'done' | 'cancelled';
const ordersFilterKey = 'boostly.orders.filter.v1';
const FILTERS: { k: FiltKey; l: string }[] = [
  { k: 'all',       l: 'All' },
  { k: 'active',    l: 'Active' },
  { k: 'done',      l: 'Done' },
  { k: 'cancelled', l: 'Cancelled' },
];

function readOrdersFilter(): FiltKey {
  try {
    const value = sessionStorage.getItem(ordersFilterKey);
    return FILTERS.some(f => f.k === value) ? value as FiltKey : 'active';
  } catch {
    return 'active';
  }
}

function writeOrdersFilter(value: FiltKey) {
  try {
    sessionStorage.setItem(ordersFilterKey, value);
  } catch {
    // ignore storage failures
  }
}

export function OrdersScreen() {
  const [filt, setFilt] = useState<FiltKey>(readOrdersFilter);
  const q = useQuery({
    queryKey: ['orders', 1],
    queryFn: () => catalogRepo.orders(1, config.orders.pageSize),
    refetchInterval: config.orders.activePollIntervalMs,
  });

  const visible = useMemo(() => {
    const list = q.data?.orders ?? [];
    if (filt === 'all') return list;
    return list.filter(o => orderState(o) === filt);
  }, [q.data, filt]);

  const groups = useMemo(() => {
    const m = new Map<string, Order[]>();
    for (const o of visible) {
      const key = dayBucket(o.startedat ?? 0);
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(o);
    }
    return m;
  }, [visible]);

  return (
    <div style={{ background: T.bg, minHeight: '100%' }}>
      <TopBar
        title="Orders"
        sub={q.data ? `${q.data.total} total` : undefined}
        right={<button style={btnIcon}>{sized(Icons.filter, 18)}</button>}
      />

      <div style={{ display: 'flex', gap: 8, padding: '14px 20px', overflowX: 'auto' }}>
        {FILTERS.map(f => (
          <Chip key={f.k} active={f.k === filt} onClick={() => {
            setFilt(f.k);
            writeOrdersFilter(f.k);
          }}>{f.l}</Chip>
        ))}
      </div>

      {q.isError && <ErrorBox message="Couldn't load orders." onRetry={() => q.refetch()}/>}
      {q.isPending && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><Spinner/></div>
      )}

      {q.data && visible.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: T.textDim, fontSize: 13 }}>
          No orders yet.
        </div>
      )}

      {[...groups.entries()].map(([day, items]) => (
        <div key={day}>
          <div style={{ padding: '8px 24px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ height: 1, flex: 1, background: T.line }}/>
            <span style={{ fontSize: 10, color: T.textMute, textTransform: 'uppercase',
              letterSpacing: 1.5, fontWeight: 600 }}>
              {day === 'today' ? 'Today' : day === 'yesterday' ? 'Yesterday' : day}
            </span>
            <div style={{ height: 1, flex: 1, background: T.line }}/>
          </div>
          <div style={{ padding: '0 20px' }}>
            <AnimatedList
              items={items}
              keyFn={(_, i) => i}
              renderItem={o => <OrderRow o={o}/>}
              batchSize={15} staggerMs={35}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function OrderRow({ o }: { o: Order }) {
  const kind: ServiceKind = classifyService(o.reference ?? {});
  const state = orderState(o);
  const rawState = normalizeOrderState(o.state);
  const icons: Record<ServiceKind, JSX.Element> = {
    like: Icons.heart, follow: Icons.follow, view: Icons.eye,
    comment: Icons.comment, other: Icons.spark,
  };
  const statusColors = {
    active:    { c: T.warn,   l: 'In progress' },
    done:      { c: T.ok,     l: 'Finished' },
    cancelled: { c: T.danger, l: 'Cancelled' },
  };
  const s = rawState === 'pendingreview'
    ? { c: T.warn, l: 'Pending review' }
    : statusColors[state];
  const id = (o as { id?: number }).id ?? (o.startedat ?? 0); // fallback for routing

  return (
    <button onClick={() => route(buildRoute.orderDetail(id))} style={{
      ...card, padding: 14, display: 'flex', alignItems: 'center', gap: 12,
      cursor: 'pointer', textAlign: 'left', width: '100%', color: T.text,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 12, background: T.surface2,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: T.text, flexShrink: 0,
      }}>{sized(icons[kind], 20)}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 14, fontWeight: 600, color: T.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{o.reference?.name ?? 'Order'}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4,
          fontSize: 11, color: T.textDim, fontFamily: T.fontMono }}>
          <span style={{ color: s.c, fontWeight: 600 }}>● {s.l}</span>
          {o.platform && <>
            <span style={{ color: T.line2 }}>·</span>
            <span>{o.platform}</span>
          </>}
        </div>
        {state === 'active' && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, height: 4, background: T.surface2, borderRadius: 2,
              overflow: 'hidden' }}>
              <div style={{
                height: '100%', width: `${o.progress ?? 0}%`,
                background: `linear-gradient(90deg, ${T.accent2}, ${T.accent})`,
                borderRadius: 2,
              }}/>
            </div>
            <span style={{ fontSize: 10, color: T.textDim, fontFamily: T.fontMono,
              fontVariantNumeric: 'tabular-nums', minWidth: 30 }}>{o.progress ?? 0}%</span>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <CoinGlyph size={11}/>
          <span style={{ fontFamily: T.fontMono, fontSize: 13, color: T.text,
            fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{fmt(orderTotalCost(o))}</span>
        </div>
        <span style={{ fontSize: 10, color: T.textMute, fontFamily: T.fontMono }}>
          {fmt(o.amount ?? 0)} units
        </span>
      </div>
    </button>
  );
}
